import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Crown, Sparkles } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  CLIENT_PREMIUM_COMING_SOON_BADGE_TEST_ID,
  CLIENT_PREMIUM_FUTURE_DURATIONS,
  CLIENT_PREMIUM_UNAVAILABLE_TEST_ID,
  PREMIUM_CLIENT_BENEFITS,
} from '../../lib/clientDashboardDisplay';
import { CLIENT_PREMIUM_PRICING_LANE_TEST_ID } from '../../lib/pricingLaneDisplay';

export default function ClientPremiumPanel({
  t,
  testId = CLIENT_PREMIUM_PRICING_LANE_TEST_ID,
  showDashboardLink = true,
  showPricingUnavailableNote = false,
  benefitTestIdPrefix = 'client-premium-benefit',
}) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-military-gold/30 bg-gradient-to-br from-amber-50 via-white to-slate-50 p-6 sm:p-8"
      data-testid={testId}
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-military-gold/15 rounded-full blur-2xl pointer-events-none" />
      <div className="relative flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-military-gold/15 border border-military-gold/25 flex items-center justify-center shrink-0">
          <Sparkles className="w-6 h-6 text-military-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
              {t('client.premium_tools_title', 'Premium Client Tools')}
              <Crown className="w-5 h-5 text-military-gold" />
            </h1>
            <Badge
              className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]"
              data-testid={CLIENT_PREMIUM_COMING_SOON_BADGE_TEST_ID}
            >
              {t('client.coming_soon', 'Coming soon')}
            </Badge>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed max-w-2xl">
            {t(
              'pricing.client_lane_intro',
              'Premium client tools are designed to help you find housing faster with early listing awareness, saved-search alerts, enhanced location insights, and relocation guidance.',
            )}
          </p>
          <p className="text-xs text-slate-500 mt-2 max-w-2xl">
            {t(
              'pricing.client_lane_note',
              'Host listing plans are for property owners only. Your premium renter tools will appear here when available.',
            )}
          </p>
          {showPricingUnavailableNote && (
            <p
              className="text-sm text-slate-700 mt-3 max-w-2xl rounded-lg border border-dashed border-slate-300 bg-white/70 px-3 py-2"
              data-testid={CLIENT_PREMIUM_UNAVAILABLE_TEST_ID}
            >
              {t(
                'settings.client_premium_unavailable',
                'Client premium plans are not available yet. We’ll add renter-focused pricing once the feature set is finalized.',
              )}
            </p>
          )}
          <ul className="mt-5 grid sm:grid-cols-2 gap-2">
            {PREMIUM_CLIENT_BENEFITS.map((benefit) => (
              <li
                key={benefit.id}
                className="flex items-start gap-2 text-sm text-slate-700"
                data-testid={`${benefitTestIdPrefix}-${benefit.id}`}
              >
                <Check className="w-4 h-4 text-military-gold shrink-0 mt-0.5" />
                {t(benefit.labelKey, benefit.defaultLabel)}
              </li>
            ))}
          </ul>
          <div className="mt-5">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">
              {t('client.premium_future_durations_title', 'Future plan lengths (conceptual)')}
            </p>
            <div className="flex flex-wrap gap-2">
              {CLIENT_PREMIUM_FUTURE_DURATIONS.map((duration) => (
                <Badge
                  key={duration.id}
                  variant="outline"
                  className="text-[11px] text-slate-600 border-slate-300"
                  data-testid={`client-premium-duration-${duration.id}`}
                >
                  {t(duration.labelKey, duration.defaultLabel)}
                </Badge>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              {t('client.premium_no_pricing_yet', 'Pricing will be announced when client premium launches.')}
            </p>
          </div>
          {showDashboardLink && (
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild variant="outline" className="border-slate-300">
                <Link to="/dashboard">{t('nav.client_home_search', 'My Home Search')}</Link>
              </Button>
              <Button asChild variant="ghost" className="text-slate-600">
                <Link to="/settings">{t('nav.settings', 'Settings')}</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
